<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { ref, computed, inject } from "vue"
import { storeToRefs } from "pinia"
import { useEventBus } from "@vueuse/core"
import { useI18n } from "vue-i18n"
import { useSubscriptionFormStore } from "@modules/abonnement/stores/subscription-form.store"
import { SUPPORT_OPEN_EVENT } from "@core/components/support/support.events"
import type {
    FormPaymentUserForm,
    FormPaymentCompanyForm,
} from "@modules/abonnement/types/subscription"
import type { Services } from "@services"
import type { PipedriveContextIds } from "@modules/abonnement/services/pipedrive.service"

// ================================
// INTERNATIONALIZATION
// ================================
const { t, tm } = useI18n({
    messages: {
        fr: {
            title: "Prêts à démarrer ?",
            subtitle:
                "Créez votre compte pour souscrire à l’une de nos offres (avec ou sans engagement)",
            chose_offer: "Accéder aux offres",
            contact_support: "Contacter le support",
            return: "Précédent",
            cgu_0: "J'accepte les",
            cgu_1: "Conditions Générales de Vente et de Services",
            already_exist:
                "Il existe déjà un compte avec l'email {username}. Veuillez réessayer avec une autre adresse email ou bien contacter notre support si besoin.",
            error_save:
                "Une erreur est survenue, vous empêchant d'aller plus loin. Veuillez réessayer plus tard ou bien contacter notre support.",
            form: {
                firstname: "Prénom",
                firstname_placeholder: "Jean",
                lastname: "Nom",
                lastname_placeholder: "Dupont",
                username: "Adresse mail",
                username_placeholder: "jean.dupont{'@'}gmail.com",
                phone: "Téléphone",
                phone_placeholder: "-- -- -- -- --",
                company_name: "Nom de l'organisation",
                company_name_placeholder: "Jean Société",
                sector: "Secteur d'activité",
                sector_options: [
                    "Installateur photovoltaïque",
                    "Installateur IRVE",
                    "Bureau d'étude",
                    "Bailleur social",
                    "Collectivité publique",
                    "Courtier en énergie",
                    "Exploitant chauffagiste",
                    "Professionnel de l'énergie",
                    "Société de tiers-financement",
                    "Secteur tertiaire",
                ],
            },
            email_invalid: "Adresse email invalide",
        },
    },
})

// ================================
// PROPS & EMITS
// ================================
const emit = defineEmits<{
    (
        e: "submit",
        payload: {
            alreadyExist: false
            user: FormPaymentUserForm & { phone: string }
            company: FormPaymentCompanyForm
        }
    ): void
    (
        e: "update:pipedrive",
        payload: {
            contactId?: string
            organisationId?: string
            dealId?: string
        }
    ): void
    (e: "return"): void
}>()

// ================================
// COMPOSABLES
// ================================
const services = inject<Services>("services")
const store = useSubscriptionFormStore()
const {
    introFormUser,
    introFormCompany,
    verifyUserResult,
    paymentContext,
    offerSelected,
    isFetchingVerifyUserAccountForPayment,
    hasErrorVerifyUserAccountForPayment,
    errorVerifyUserAccountForPayment,
} = storeToRefs(store)
const {
    fetchVerifyUserAccountForPayment,
    clearVerifyUserResult,
    setPaymentContext,
} = store
/** Event bus pour ouvrir la modal support. */
const busSupportOpen = useEventBus<void>(SUPPORT_OPEN_EVENT)

// ================================
// STATE (vue locale pour l’affichage)
// ================================
type ViewState = "form" | "alreadyExist"

/** Vue courante : formulaire ou message « compte déjà existant ». */
const viewState = ref<ViewState>("form")

/** True pendant l'appel Pipedrive (createContact/updateContact). */
const isPipedriveLoading = ref(false)

/** Erreur renvoyée par l'appel Pipedrive (affichée sous le formulaire). */
const pipedriveError = ref<Error | null>(null)

// ================================
// BUSINESS LOGIC (validation)
// ================================
/** Vérifie le format email. */
function validateEmail(value: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value ?? "")
}

/** Vérifie que le numéro contient au moins 10 chiffres. */
function validatePhone(value: string): boolean {
    const digits = (value ?? "").replace(/\D/g, "")
    return digits.length >= 10
}

// ================================
// COMPUTED
// ================================
/** Options du select secteur (secteurs d'activité). */
const getSectorOptions = computed(() => {
    const options = tm("form.sector_options")
    const list = Array.isArray(options) ? options : []
    return list.map((label: string) => ({ label, value: label }))
})

/** True si le formulaire (étape 1) est valide (champs requis + email + téléphone). */
const getIsFirstStepValid = computed(() => {
    const f = introFormUser.value
    const c = introFormCompany.value
    return (
        !!f.agree &&
        !!f.firstname?.trim() &&
        !!f.lastname?.trim() &&
        !!f.sector &&
        !!c.name?.trim() &&
        validatePhone(f.phone) &&
        validateEmail(f.username)
    )
})

/** Message d'erreur pour le champ email (si invalide). */
const getEmailError = computed(() => {
    if (!introFormUser.value.username) return null
    return validateEmail(introFormUser.value.username)
        ? null
        : t("email_invalid")
})

/** Message « compte déjà existant » avec email. */
const getAlreadyExistMessage = computed(() =>
    t("already_exist", { username: introFormUser.value.username || "" })
)

/** Message d’erreur à afficher (store ou fallback). */
const getSaveErrorMessage = computed(() => {
    const pipedriveErr = pipedriveError.value
    if (pipedriveErr?.message) return pipedriveErr.message
    const err = errorVerifyUserAccountForPayment.value
    if (err?.message) return err.message
    return t("error_save")
})

/** True si un appel asynchrone est en cours (vérification email ou Pipedrive). */
const getIsSubmitLoading = computed(
    () =>
        isFetchingVerifyUserAccountForPayment.value || isPipedriveLoading.value
)

/** True si une erreur (vérification ou Pipedrive) doit être affichée. */
const getHasSubmitError = computed(
    () => hasErrorVerifyUserAccountForPayment.value || !!pipedriveError.value
)

// ================================
// INTERACTIONS
// ================================
/** Soumet le formulaire (vérification compte existant, puis Pipedrive si nouveau compte, puis submit). */
async function onSubmit(): Promise<void> {
    if (!getIsFirstStepValid.value) return
    const username = introFormUser.value.username?.trim()
    if (!username) return
    pipedriveError.value = null
    await fetchVerifyUserAccountForPayment({ username })
    if (verifyUserResult.value === true) {
        viewState.value = "alreadyExist"
        const pipedrive = paymentContext.value?.pipedrive as
            | { contactId?: string; organisationId?: string; dealId?: string }
            | undefined
        emit("update:pipedrive", {
            contactId: pipedrive?.contactId,
            organisationId: pipedrive?.organisationId,
            dealId: pipedrive?.dealId,
        })
        return
    }
    if (hasErrorVerifyUserAccountForPayment.value) {
        return
    }
    const userPayload: FormPaymentUserForm & { phone: string } = {
        ...introFormUser.value,
        phone:
            introFormUser.value.phone.replace(/\D/g, "").trim() ||
            introFormUser.value.phone,
    }
    const companyPayload = { ...introFormCompany.value }
    const product = offerSelected.value || null
    const existingPipedrive = paymentContext.value?.pipedrive as
        | { contactId?: string; organisationId?: string; dealId?: string }
        | undefined
    const contactId = existingPipedrive?.contactId
    if (!services?.$createContact || !services?.$updateContact) {
        pipedriveError.value = new Error("Service Pipedrive non disponible")
        return
    }
    isPipedriveLoading.value = true
    try {
        let pipedriveIds: PipedriveContextIds
        if (contactId != null && contactId !== "") {
            const data = await services.$updateContact({
                id: contactId,
                dealId: existingPipedrive?.dealId,
                firstname: userPayload.firstname,
                lastname: userPayload.lastname,
                username: userPayload.username,
                phone: userPayload.phone,
                activitySector: userPayload.sector,
                product,
                organization: companyPayload.name,
            })
            pipedriveIds = {
                contactId: data?.contact?.id ?? contactId,
                organisationId:
                    data?.organization?.id ?? existingPipedrive?.organisationId,
                dealId: data?.deal?.id ?? existingPipedrive?.dealId,
            }
        } else {
            const data = await services.$createContact({
                firstname: userPayload.firstname,
                lastname: userPayload.lastname,
                username: userPayload.username,
                phone: userPayload.phone,
                activitySector: userPayload.sector,
                product,
                organization: companyPayload.name,
            })
            pipedriveIds = {
                contactId: data?.contact?.id,
                organisationId: data?.contact?.organization?.id,
                dealId: data?.deal?.id,
            }
        }
        setPaymentContext({
            user: userPayload as unknown as Record<string, unknown>,
            company: companyPayload as unknown as Record<string, unknown>,
            pipedrive: pipedriveIds,
        })
        emit("update:pipedrive", {
            contactId: pipedriveIds.contactId,
            organisationId: pipedriveIds.organisationId,
            dealId: pipedriveIds.dealId,
        })
        emit("submit", {
            alreadyExist: false,
            user: userPayload,
            company: companyPayload,
        })
    } catch (err) {
        pipedriveError.value =
            err instanceof Error ? err : new Error(String(err))
    } finally {
        isPipedriveLoading.value = false
    }
}

/** Retour à la vue formulaire (compte déjà existant). */
function onBack(): void {
    viewState.value = "form"
    clearVerifyUserResult()
    emit("return")
}

/**
 * Ouvre la modal support (Contacter le support).
 */
function onContactSupport(): void {
    busSupportOpen.emit()
}
</script>

<template>
    <BaseCard
        class="subscription-form-user"
        :pt="{
            header: 'flex flex-col gap-4 justify-center items-center p-6',
            content: 'flex flex-col gap-4',
        }"
    >
        <template #header>
            <h1 class="text-xl font-semibold text-primary-900">
                {{ t("title") }}
            </h1>
            <p>
                {{ t("subtitle") }}
            </p>
        </template>
        <template #content>
            <!-- Formulaire principal -->
            <template v-if="viewState !== 'alreadyExist'">
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <BaseInputWrapper
                        input-id="form-payment-firstname"
                        :label="t('form.firstname')"
                        unfloat
                    >
                        <BaseInputText
                            id="form-payment-firstname"
                            v-model="introFormUser.firstname"
                            type="text"
                            :placeholder="t('form.firstname_placeholder')"
                            class="w-full"
                        />
                    </BaseInputWrapper>

                    <BaseInputWrapper
                        input-id="form-payment-lastname"
                        :label="t('form.lastname')"
                        unfloat
                    >
                        <BaseInputText
                            id="form-payment-lastname"
                            v-model="introFormUser.lastname"
                            type="text"
                            :placeholder="t('form.lastname_placeholder')"
                            class="w-full"
                        />
                    </BaseInputWrapper>

                    <BaseInputWrapper
                        input-id="form-payment-username"
                        :label="t('form.username')"
                        :error="getEmailError ?? undefined"
                        unfloat
                        class="sm:col-span-2"
                    >
                        <BaseInputText
                            id="form-payment-username"
                            v-model="introFormUser.username"
                            type="email"
                            :placeholder="t('form.username_placeholder')"
                            class="w-full"
                        />
                    </BaseInputWrapper>

                    <BaseInputWrapper
                        input-id="form-payment-phone"
                        :label="t('form.phone')"
                        unfloat
                    >
                        <BaseInputMask
                            id="form-payment-phone"
                            v-model="introFormUser.phone"
                            mask="99 99 99 99 99"
                            :placeholder="t('form.phone_placeholder')"
                            unmask
                            class="w-full"
                        />
                    </BaseInputWrapper>

                    <BaseInputWrapper
                        input-id="form-payment-company-name"
                        :label="t('form.company_name')"
                        unfloat
                    >
                        <BaseInputText
                            id="form-payment-company-name"
                            v-model="introFormCompany.name"
                            type="text"
                            :placeholder="t('form.company_name_placeholder')"
                            class="w-full"
                            data-cy="company-name"
                        />
                    </BaseInputWrapper>

                    <BaseInputWrapper
                        input-id="form-payment-sector"
                        :label="t('form.sector')"
                        unfloat
                        class="sm:col-span-2"
                    >
                        <BaseSelect
                            id="form-payment-sector"
                            v-model="introFormUser.sector"
                            :options="getSectorOptions"
                            option-label="label"
                            option-value="value"
                            :placeholder="t('form.sector')"
                            class="w-full"
                        />
                    </BaseInputWrapper>
                </div>

                <div
                    class="flex flex-wrap items-center justify-center gap-2 mt-4"
                >
                    <BaseCheckbox
                        id="form-payment-agree"
                        v-model="introFormUser.agree"
                        :binary="true"
                    />
                    <label
                        for="form-payment-agree"
                        class="text-sm text-primary-700"
                    >
                        {{ t("cgu_0") }}
                        <a
                            href="https://www.homeys.fr/mentions-legales"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="text-primary-600 underline"
                        >
                            {{ t("cgu_1") }} </a
                        >.
                    </label>
                </div>

                <!-- État de chargement et erreur de la vérification email ou Pipedrive -->
                <BaseMessage v-if="getHasSubmitError" severity="error">
                    {{ getSaveErrorMessage }}
                </BaseMessage>
                <div class="flex justify-center mt-6">
                    <BaseButton
                        :label="t('chose_offer')"
                        :disabled="!getIsFirstStepValid"
                        :loading="getIsSubmitLoading"
                        @click="onSubmit"
                    />
                </div>
            </template>

            <!-- Compte déjà existant -->
            <div
                v-else
                class="flex flex-col items-center justify-center gap-8 text-center p-4"
            >
                <BaseMessage severity="info">
                    {{ getAlreadyExistMessage }}
                </BaseMessage>
                <div class="flex gap-2 flex-wrap justify-center">
                    <BaseButton
                        :label="t('contact_support')"
                        @click="onContactSupport"
                    />
                    <BaseButton
                        :label="t('return')"
                        severity="secondary"
                        outlined
                        @click="onBack"
                    />
                </div>
            </div>
        </template>
    </BaseCard>
</template>
